import { useTranslation } from 'react-i18next';
import { SlideToConfirm } from './SlideToConfirm';
import { Button } from '../kit/Button';
import { formatISTTime } from '../../lib/format';

type StepState = 'done' | 'current' | 'locked';

type Props = {
  reachedAt?: string | null;
  startedAt?: string | null;
  /** Disables the step CTAs while a one-tap event is posting (each tap records
   *  the device time immediately — no confirmation sheet). */
  submitting?: boolean;
  onReached: () => void;
  onStartRide: () => void;
  onRelease: () => void;
};

const CIRCLE = 26;

function StepNode({ state, label, at }: { state: StepState; label: string; at?: string | null }) {
  const circle = {
    width: CIRCLE,
    height: CIRCLE,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
  } as const;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 62, flex: '0 0 auto' }}>
      {state === 'done' ? (
        <div style={{ ...circle, background: 'var(--color-primary)' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
            <polyline points="20 6 9 17 4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      ) : state === 'current' ? (
        <div style={{ ...circle, border: '2px solid var(--color-primary)' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)' }} />
        </div>
      ) : (
        <div style={{ ...circle, border: '1.5px solid var(--color-border)' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
            <rect x="5" y="11" width="14" height="9" rx="2" stroke="var(--color-text-secondary)" strokeWidth="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="var(--color-text-secondary)" strokeWidth="2" />
          </svg>
        </div>
      )}
      <span
        style={{
          fontSize: 11,
          marginTop: 6,
          textAlign: 'center',
          lineHeight: 1.3,
          fontWeight: state === 'current' ? 600 : 400,
          color:
            state === 'done'
              ? 'var(--green-700)'
              : state === 'current'
                ? 'var(--color-text)'
                : 'var(--color-text-secondary)',
        }}
      >
        {label}
        {at ? (
          <>
            <br />
            <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>{formatISTTime(at)}</span>
          </>
        ) : null}
      </span>
    </div>
  );
}

/**
 * In-flight trip action area: a 3-step progress stepper (Reached → Start ride →
 * Release) plus a single primary action — the current step. Completed steps
 * collapse into the stepper rather than staying as tappable rows, and the
 * Slide-to-Release control is gated behind "Start ride" so a trip can't be
 * released before the ride is marked started.
 */
export function TripProgressBar({ reachedAt, startedAt, submitting, onReached, onStartRide, onRelease }: Props) {
  const { t } = useTranslation();
  const reachedDone = !!reachedAt;
  const startedDone = !!startedAt;

  const reachedState: StepState = reachedDone ? 'done' : 'current';
  const startState: StepState = startedDone ? 'done' : reachedDone ? 'current' : 'locked';
  const releaseState: StepState = startedDone ? 'current' : 'locked';

  const connector = (filled: boolean) =>
    ({ flex: '1 1 auto', height: 2, marginTop: CIRCLE / 2 - 1, background: filled ? 'var(--color-primary)' : 'var(--color-border)' }) as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <StepNode state={reachedState} label={t('stepReached', { defaultValue: 'Reached' })} at={reachedAt} />
        <div style={connector(reachedDone)} />
        <StepNode state={startState} label={t('stepPickedUp', { defaultValue: 'Picked up' })} at={startedAt} />
        <div style={connector(startedDone)} />
        <StepNode state={releaseState} label={t('stepRelease', { defaultValue: 'Release' })} />
      </div>

      {!reachedDone && (
        <Button variant="primary" onClick={onReached} loading={submitting}>
          {t('reachedPickup', { defaultValue: "I've reached the pickup" })}
        </Button>
      )}

      {reachedDone && !startedDone && (
        <>
          <Button variant="primary" onClick={onStartRide} loading={submitting}>
            {t('passengerPickedUp', { defaultValue: 'Passenger picked up' })}
          </Button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              border: '1px dashed var(--color-border)',
              borderRadius: 14,
              padding: 12,
              color: 'var(--color-text-secondary)',
              fontSize: 13,
            }}
          >
            {t('releaseLockedHint', { defaultValue: 'Release unlocks after you start the ride' })}
          </div>
        </>
      )}

      {startedDone && (
        <SlideToConfirm
          label={t('slideToEnd', { defaultValue: 'Slide to Release Trip' })}
          loadingLabel={t('endingTrip', { defaultValue: 'Releasing trip…' })}
          onConfirm={onRelease}
        />
      )}
    </div>
  );
}
